/**
 * Dialog "Segnala al catalogo": l'organizzazione segnala un problema su un
 * materiale condiviso (prezzo errato, dato mancante, fuori produzione...).
 * La segnalazione arriva all'admin di piattaforma nell'area Admin.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Flag } from 'lucide-react';
import { DatabaseMaterial } from '@/hooks/useMaterials';
import {
  useCreateChangeRequest,
  CHANGE_REQUEST_TYPE_LABEL,
  type ChangeRequestType,
} from '@/hooks/useMaterialChangeRequests';

interface Props {
  material: DatabaseMaterial | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReportMaterialDialog: React.FC<Props> = ({ material, open, onOpenChange }) => {
  const { mutate: send, isPending } = useCreateChangeRequest();
  const [type, setType] = useState<ChangeRequestType>('prezzo');
  const [message, setMessage] = useState('');
  const [suggested, setSuggested] = useState('');

  const handleSend = () => {
    if (!material) return;
    send(
      { materialId: material.id, requestType: type, message, suggestedValue: suggested },
      {
        onSuccess: () => {
          setMessage('');
          setSuggested('');
          setType('prezzo');
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-4 w-4 text-amber-600" />
            Segnala al catalogo
          </DialogTitle>
          <DialogDescription>
            {material ? `${material.name} (${material.code})` : ''} — la segnalazione viene inviata
            al team DryConfig, che aggiornerà il catalogo condiviso.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs">Tipo di segnalazione</Label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as ChangeRequestType)}
              className="w-full px-3 py-2 border border-input rounded-md text-sm"
              disabled={isPending}
            >
              {(Object.keys(CHANGE_REQUEST_TYPE_LABEL) as ChangeRequestType[]).map((k) => (
                <option key={k} value={k}>{CHANGE_REQUEST_TYPE_LABEL[k]}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mcr-msg" className="text-xs">Descrizione *</Label>
            <Textarea
              id="mcr-msg"
              rows={4}
              placeholder="Es. il prezzo di listino non corrisponde a quello del listino ufficiale di maggio…"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={isPending}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="mcr-sug" className="text-xs">Valore corretto (facoltativo)</Label>
            <Input
              id="mcr-sug"
              placeholder="Es. 9,23 €/mq"
              value={suggested}
              onChange={(e) => setSuggested(e.target.value)}
              disabled={isPending}
            />
          </div>

          <p className="text-[11px] text-muted-foreground">
            Serve solo un tuo prezzo diverso? Non serve una segnalazione: usa
            <strong> Personalizzazione</strong> nella scheda del materiale.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Annulla
          </Button>
          <Button onClick={handleSend} disabled={isPending || !message.trim()}>
            {isPending ? 'Invio…' : 'Invia segnalazione'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportMaterialDialog;
